import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/ProductsPage.css';
import '../../styles/Filters.css';
import '../../styles/StarRating.css';
import { formatCurrency } from '../../utils/format';
import { useCart } from '../../contexts/CartContext';
import { useQuickView } from '../../contexts/QuickViewContext';
import { productsAPI } from '../../api';
import ProductFilters from './ProductFilters';
import QuickViewModal from './QuickViewModal';
import StarRating from '../ui/StarRating';
import ShareMenu from '../ui/ShareMenu';

const ProductsPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToCart } = useCart();
  const { isQuickViewModalOpen, openQuickViewModal, closeQuickViewModal } = useQuickView();
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [ratings, setRatings] = useState({});
  const [quantities, setQuantities] = useState({});
  const [favorites, setFavorites] = useState([]);

  const handleRatingChange = (productId, rating) => {
    setRatings(prev => ({ ...prev, [productId]: rating }));
  };

  const handleQuantityChange = (productId, quantity) => {
    const newQuantity = Math.max(1, quantity);
    setQuantities(prev => ({ ...prev, [productId]: newQuantity }));
  };

  const toggleFavorite = (productId) => {
    setFavorites(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const [filters, setFilters] = useState({
    price: { min: '', max: '' },
    materiais: []
  });

  const availableFilters = {
    materiais: ['Prata 925', 'Ouro 18k', 'Aço Inoxidável', 'Zircônia']
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const productList = Array.isArray(products) ? products : [];
  const filteredProducts = productList.filter(product => {
    if (filters.price.min && product.price < parseFloat(filters.price.min)) return false;
    if (filters.price.max && product.price > parseFloat(filters.price.max)) return false;
    if (filters.materiais.length > 0 && !filters.materiais.includes(product.material)) return false;
    return true;
  });

  const fetchProducts = useCallback(async (categoryFilter) => {
    try {
      setLoading(true);
      setError(null);
      const data = await productsAPI.getAll(categoryFilter);
      setProducts(data);
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts(category);
  }, [category, fetchProducts]);

  const handleBuyNow = (product, quantity) => {
    addToCart(product, quantity);
    navigate('/checkout');
  };

  const categoryTitle = category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Catálogo';

  if (loading) return <div className="loading"><p>Carregando produtos...</p></div>;
  if (error) return <div className="error-container"><p>Erro ao carregar produtos: {error}</p></div>;

  return (
    <>
      <div className="products-page-container">
        <ProductFilters
          filters={filters}
          setFilters={setFilters}
          availableFilters={availableFilters}
          handleFilterChange={handleFilterChange}
          className="product-filters-mobile" // Added className
        />

        <div className="main-content">
          <div className="products-header">
            <div className="breadcrumb">
              Home / {categoryTitle}
            </div>
            <div className="view-options">
              <select name="sort-by" id="sort-by">
                <option value="position">Posição</option>
                <option value="price-asc">Preço: Menor ao Maior</option>
                <option value="price-desc">Preço: Maior ao Menor</option>
              </select>
              <select name="display" id="display">
                <option value="6">6 por página</option>
                <option value="12">12 por página</option>
                <option value="24">24 por página</option>
              </select>
            </div>
          </div>

          <div className="products-grid">
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => {
                const imageFolder = category || product.folder || product.category;
                const isHovered = product.id === hoveredProductId;
                const imageName = isHovered && product.image_hover ? product.image_hover : product.image;
                const productQuantity = quantities[product.id] || 1;
                const isFavorited = favorites.includes(product.id);

                return (
                  <div
                    key={product.id}
                    className="product-card"
                    onMouseEnter={() => setHoveredProductId(product.id)}
                    onMouseLeave={() => setHoveredProductId(null)}
                  >
                    <div className="product-image-container">
                      <img
                        src={`/products/${imageFolder}/${imageName}`}
                        alt={product.name}
                        loading="lazy"
                        onClick={() => openQuickViewModal(product)}
                        className={isHovered ? 'product-image-zoom' : ''}
                      />
                      <div className="product-actions">
                        <button
                          className={`favorite ${isFavorited ? 'favorited' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(product.id);
                          }}
                          aria-label={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                        >
                          ♥
                        </button>
                        <ShareMenu product={product} />
                      </div>
                    </div>

                    <h3>{product.name}</h3>
                    <StarRating rating={ratings[product.id] || 0} setRating={(rating) => handleRatingChange(product.id, rating)} />

                    {isHovered && (
                      <div className="buy-actions">
                        <button
                          className="btn-quick-view"
                          onClick={(e) => { e.stopPropagation(); openQuickViewModal(product); }}
                        >
                          Visualização Rápida
                        </button>
                        <button
                          className="btn-buy-now"
                          onClick={(e) => { e.stopPropagation(); handleBuyNow(product, productQuantity); }}
                          disabled={product.stock === 0}
                        >
                          COMPRAR
                        </button>
                        <button
                          className="btn-add-to-cart"
                          onClick={(e) => { e.stopPropagation(); addToCart(product, productQuantity); }}
                          disabled={product.stock === 0}
                        >
                          ADICIONAR AO CARRINHO
                        </button>
                      </div>
                    )}

                    {!isHovered && (
                      <>
                        <div className="product-prices">
                          <span className="price-total">{formatCurrency(product.price)}</span>
                          <span className="price-installments">ou 10x de {formatCurrency(product.price / 10)}</span>
                        </div>
                        <div className="product-stock">
                          <span>{product.stock} em estoque</span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            ) : (
              <p>Nenhum produto encontrado.</p>
            )}
          </div>

          <div className="pagination">
            <span>1</span>
            <button>2</button>
            <button className="next-page">Próximo</button>
          </div>
        </div>
      </div>
      {isQuickViewModalOpen && (
        <QuickViewModal onClose={closeQuickViewModal} />
      )}
    </>
  );
};

export default ProductsPage;